/* eslint-disable no-case-declarations */
import 'babel-polyfill'
import React from 'react'
import { render } from 'react-dom'
import { Provider } from 'react-redux'
import {
  CallbackComponent,
  createUserManager,
  OidcProvider,
  processSilentRenew
} from 'redux-oidc'
import App from './app/components/App'
import { setReduxStore, store } from './app/redux/state'
import {
  setUserManagerAuthorizationUrl,
  userManagerConfig
} from './app/helpers/userManager'
import { setServerConfig } from './app/constants/config'
import 'whatwg-fetch'
import './assets/scss/main.scss'
import { setBaseUrls } from './app/helpers/baseUrl'
import ReactGA from 'react-ga'
import { setCookie, updateObject } from './app/helpers/util'
import { logGeneralError } from './app/api/error-logger.api'
import MaintenancePage from './app/components/Authentication/MaintenancePage'
import TagManager from 'react-gtm-module'
import { setDiffFromServerTime } from './app/helpers/dateFormatter'

function setConsole(server) {
  if (server === 'production') {
    console.log = () => {}
    console.warn = () => {}
    console.error = () => {}
  }
}

function activateGoogleAnalitycs(server, trackingCode) {
  if (server === 'production') {
    ReactGA.initialize(trackingCode)
    let ga = ReactGA.ga()
    ga('set', 'allowAdFeatures', false)
  }
}

function activateGoogleTagManager(server, gtmId) {
  if (server === 'production') {
    TagManager.initialize({
      gtmId
    })
  }
}

function getQueryVariable(variable) {
  var query = window.location.search.substring(1)
  var vars = query.split('&')
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=')
    if (pair[0] === variable) {
      return pair[1]
    }
  }
  return false
}

const fetchConfig = () =>
  new Promise((resolve, reject) => {
    xhrConfig = new XMLHttpRequest()
    xhrConfig.open('GET', '/config.json', true)
    xhrConfig.setRequestHeader('Cache-Control', 'no-cache')
    xhrConfig.onload = resolve
    xhrConfig.onerror = reject // () => reject(xhrConfig.statusText); //  console.error(xhrConfig.statusText);
    xhrConfig.send(null)
  })

// javascript
// 1) Improve iOS detection (covers iPadOS desktop UA)
function isIOS() {
  const ua = navigator.userAgent || ''
  const platform = navigator.platform || ''
  const iOSDevice = /iPhone|iPad|iPod/i.test(ua)
  const iPadOSDesktopUA =
    platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return iOSDevice || iPadOSDesktopUA
}

function isAndroid() {
  return /Android/i.test(navigator.userAgent || '')
}

// 2) Detect common in‑app browsers (FB, Instagram, Telegram, TikTok, etc.)
function isInAppBrowser() {
  const ua = (navigator.userAgent || navigator.vendor || '').toLowerCase()
  return /fb_iab|fban|fbav|instagram|line|twitter|micromessenger|wechat|snapchat|tiktok|gsa|pinterest|linkedinapp|telegram|yaapp/i.test(
    ua
  )
}

// 3) Common deep‑link attempt with store fallback; cancels fallback when app opens
function attemptDeepLink({
  iosUrl,
  intentUrl,
  appStoreUrl,
  playStoreUrl,
  delay = 2500
}) {
  const inApp = isInAppBrowser()

  if (isIOS()) {
    // For in-app browsers, use a hidden iframe to attempt deep link
    // This prevents the "page can't be loaded" error
    if (inApp) {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = iosUrl
      document.body.appendChild(iframe)

      setTimeout(() => {
        document.body.removeChild(iframe)
        // Always redirect to store in in-app browsers since we can't detect app open
        window.location.href = appStoreUrl
      }, delay)
    } else {
      // Normal browser - use existing logic
      window.location.href = iosUrl
      setTimeout(() => {
        window.location.href = appStoreUrl
      }, delay)
    }
  } else if (isAndroid()) {
    // Android intent handles fallback natively
    window.location.href = intentUrl

    // Extra fallback for in-app browsers
    if (inApp) {
      setTimeout(() => {
        window.location.href = playStoreUrl
      }, delay)
    }
  }
}

// 4) Render a minimal interstitial with a tap‑to‑open button for in‑app browsers
function renderPromotionOpenInAppPage(path, query) {
  // Ensure readable viewport for the interstitial
  let viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport) {
    viewport = document.createElement('meta');
    viewport.name = 'viewport';
    document.head.appendChild(viewport);
  }
  viewport.setAttribute(
    'content',
    'width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5, user-scalable=yes, viewport-fit=cover'
  );
  const playStoreUrl =
    'https://play.google.com/store/apps/details?id=ge.lb.mobilebank'
  const appStoreUrl =
    'https://apps.apple.com/us/app/liberty-mobile/id1455225055'

  const iosPayload = encodeURI(`${path.replace(/^\//, '')}${query}`)
  const iosUrl = `libertymobilebank://${iosPayload}`

  const host = window.location.host
  const proto = 'https'
  const pathAndQuery = `${path}${query}`.replace(/^\//, '')
  const intentUrl = `intent://${host}/${pathAndQuery}#Intent;scheme=${proto};package=ge.lb.mobilebank;S.browser_fallback_url=${encodeURIComponent(
    playStoreUrl
  )};end`

  // Minimal inline UI without React mount
  document.body.innerHTML = `
      <style>
      @font-face {
        font-family: 'LB-Nu-Medium';
        src: url('/fonts/LB-Nu-Medium.woff2') format('woff2'),
             url('/fonts/LB-Nu-Medium.woff') format('woff'),
             url('/fonts/LB-Nu-Medium.ttf') format('truetype');
        font-weight: 500;
        font-style: normal;
      }
      @font-face {
        font-family: 'LB-Nu-SemiBold';
        src: url('/fonts/LB-Nu-SemiBold.woff2') format('woff2'),
             url('/fonts/LB-Nu-SemiBold.woff') format('woff'),
             url('/fonts/LB-Nu-SemiBold.ttf') format('truetype');
        font-weight: 600;
        font-style: normal;
      }
      body {
        margin: auto;
        padding: 0;
        min-width: auto !important;
      
      }
      html {
      min-width: auto !important;
      }
    </style>
<!--    <script>-->
<!--    var intViewportWidth = window.innerWidth-->
<!--    if (intViewportWidth < 1200) {-->
<!--      document-->
<!--        .querySelector('meta[name="viewport"]')-->
<!--        .setAttribute(-->
<!--          'content',-->
<!--          'width=device-width,minimum-scale=1,maximum-scale=1,initial-scale=1,user-scalable=yes viewport-fit=cover'-->
<!--        )-->
<!--    }-->


</script>
    <div
      style="
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        height: 100vh;
      "
    >
      <div style="width: 96px; height: 96px">
          <svg
            width="96"
            height="96"
            viewBox="0 0 96 96"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_65681_102253)">
              <path
                d="M64 12H32C20.9543 12 12 20.9543 12 32V64C12 75.0457 20.9543 84 32 84H64C75.0457 84 84 75.0457 84 64V32C84 20.9543 75.0457 12 64 12Z"
                fill="black"
              />
              <g clipPath="url(#clip1_65681_102253)">
                <path
                  d="M26.111 48.4001V50.77H20V39.9993H22.3847V48.4001H26.111Z"
                  fill="white"
                />
                <path
                  d="M30.2982 39.9996H27.9136V50.7703H30.2982V39.9996Z"
                  fill="white"
                />
                <path
                  d="M37.5588 45.1849C38.1253 44.6768 38.4683 43.9694 38.4683 43.077C38.4683 41.2147 36.9927 39.9996 35.1741 39.9996H32.1025V42.3074H35.1741C35.7257 42.3074 36.0837 42.7076 36.0837 43.2614C36.0837 43.8152 35.7113 44.2154 35.1741 44.2154H33.8511V46.3999H35.4723C36.0683 46.3999 36.4561 46.8309 36.4561 47.431C36.4561 48.0311 36.0683 48.4616 35.4723 48.4616H33.8511V50.7698H35.4723C37.3357 50.7698 38.8407 49.5235 38.8407 47.6154C38.8407 46.5232 38.3339 45.6925 37.5588 45.1844"
                  fill="#DA291C"
                />
                <path
                  d="M47.2334 48.4004V50.7703H40.6753V39.9996H47.1587V42.369H43.06V44.1538H46.7863V46.4929H43.06V48.4004H47.2334Z"
                  fill="white"
                />
                <path
                  d="M65.0932 42.369H62.4103V50.7703H60.0256V42.369H57.3428V39.9996H65.0932V42.369Z"
                  fill="white"
                />
                <path
                  d="M72.4978 46.631V50.7702H70.1132V46.631L66.6104 40H69.3231L71.305 44.1537L73.2874 40H76.0002L72.4978 46.631Z"
                  fill="white"
                />
                <path
                  d="M53.6272 46.8155C54.7747 46.2155 55.5498 45.0154 55.5498 43.692C55.5498 41.6462 53.9552 39.9996 51.9728 39.9996H48.8979V42.3069H51.9728C52.6285 42.3069 53.1651 42.907 53.1651 43.692C53.1651 44.4769 52.6285 45.077 51.9728 45.077H50.444V47.2307H51.3172L53.2846 50.7698H55.8485L53.6276 46.8155H53.6272Z"
                  fill="white"
                />
                <path
                  d="M20 56.9053V53.2688H21.6002C21.7575 53.2688 21.9021 53.2934 22.0351 53.343C22.1677 53.3921 22.2811 53.46 22.3749 53.5457C22.4691 53.6319 22.5429 53.735 22.597 53.8559C22.6507 53.9767 22.6777 54.1087 22.6777 54.2522C22.6777 54.4179 22.6348 54.5686 22.5485 54.7054C22.4621 54.8421 22.3483 54.9519 22.2064 55.0348V55.068C22.3758 55.1475 22.5125 55.2587 22.6157 55.4022C22.7188 55.5453 22.7706 55.7095 22.7706 55.8934C22.7706 56.0398 22.7435 56.1752 22.6899 56.2994C22.6357 56.4237 22.5601 56.5311 22.4631 56.6216C22.366 56.7121 22.2503 56.7825 22.1163 56.8316C21.9824 56.8807 21.8349 56.9057 21.6749 56.9057H20V56.9053ZM20.7957 54.7434H21.5078C21.6189 54.7434 21.708 54.7068 21.7761 54.6336C21.8438 54.5604 21.8779 54.4651 21.8779 54.3471C21.8779 54.2291 21.8447 54.1289 21.7785 54.0557C21.7122 53.9825 21.6221 53.9459 21.5078 53.9459H20.7957V54.7429V54.7434ZM20.7957 56.2277H21.5727C21.6931 56.2277 21.7883 56.1887 21.8592 56.1106C21.9301 56.0326 21.9656 55.93 21.9656 55.8029C21.9656 55.6758 21.9301 55.5684 21.8592 55.4904C21.7883 55.4123 21.6926 55.3733 21.5727 55.3733H20.7957V56.2277Z"
                  fill="white"
                />
                <path
                  d="M23.4219 56.9053L24.6707 53.2688H25.5863L26.8164 56.9053H25.961L25.6973 56.0413V56.0268H24.5134V56.0413L24.2451 56.9053H23.4219ZM24.7309 55.3444V55.3685H25.4845V55.3444C25.3613 54.9341 25.2363 54.5281 25.1098 54.1275L24.7304 55.3444H24.7309Z"
                  fill="white"
                />
                <path
                  d="M28.7173 56.9625C28.4583 56.9625 28.2147 56.9172 27.9865 56.8267C27.7583 56.7362 27.538 56.5999 27.3252 56.4188L27.7228 55.7744C27.8371 55.8635 27.9403 55.9391 28.0327 56.0013C28.1251 56.0634 28.2128 56.1144 28.2963 56.1539C28.3794 56.1939 28.4597 56.2223 28.5367 56.2396C28.6137 56.257 28.6921 56.2661 28.7723 56.2661C28.9483 56.2661 29.0836 56.2286 29.1793 56.1539C29.2749 56.0793 29.3225 55.9815 29.3225 55.8606C29.3225 55.7398 29.2908 55.654 29.2278 55.5934C29.1648 55.5332 29.0542 55.4869 28.8969 55.4547C28.7798 55.4325 28.665 55.4104 28.5525 55.3882C28.4401 55.366 28.3253 55.342 28.2081 55.3164C27.9491 55.2721 27.7517 55.1638 27.6159 54.9919C27.4801 54.8199 27.4125 54.6071 27.4125 54.3523C27.4125 54.1775 27.4419 54.019 27.5002 53.8775C27.5585 53.7359 27.6435 53.616 27.7545 53.5172C27.8656 53.4185 28.0005 53.3429 28.1591 53.2904C28.3178 53.2379 28.4975 53.2114 28.6977 53.2114C28.9506 53.2114 29.1928 53.2528 29.4238 53.3357C29.6548 53.4185 29.8522 53.5331 30.0155 53.6795L29.6226 54.3143C29.3048 54.0407 28.9828 53.9039 28.6561 53.9039C28.4989 53.9039 28.3771 53.9381 28.2907 54.0065C28.2044 54.0749 28.1615 54.163 28.1615 54.2714C28.1615 54.3735 28.1899 54.451 28.2469 54.5054C28.3038 54.5594 28.3971 54.6008 28.5269 54.6297C28.6347 54.6519 28.7425 54.6735 28.8503 54.6942C28.9581 54.7149 29.0663 54.7366 29.1741 54.7588C29.4793 54.8127 29.7099 54.9273 29.8657 55.1021C30.0211 55.277 30.0991 55.5014 30.0991 55.7749C30.0991 55.9497 30.0669 56.1106 30.002 56.257C29.9371 56.4034 29.8457 56.5291 29.7267 56.6341C29.6077 56.739 29.463 56.82 29.2917 56.8777C29.1209 56.9351 28.9287 56.9635 28.7159 56.9635L28.7173 56.9625Z"
                  fill="white"
                />
                <path
                  d="M30.9644 56.9053V53.2688H31.7605V56.9053H30.9644Z"
                  fill="white"
                />
                <path
                  d="M33.9565 56.9625C33.6975 56.9625 33.4539 56.9172 33.2257 56.8267C32.9975 56.7362 32.7773 56.5999 32.5645 56.4188L32.9621 55.7744C33.0764 55.8635 33.1795 55.9391 33.2719 56.0013C33.3643 56.0634 33.4521 56.1144 33.5356 56.1539C33.6187 56.1939 33.6989 56.2223 33.7759 56.2396C33.8529 56.257 33.9313 56.2661 34.0116 56.2661C34.1875 56.2661 34.3229 56.2286 34.4185 56.1539C34.5142 56.0793 34.5618 55.9815 34.5618 55.8606C34.5618 55.7398 34.5301 55.654 34.4671 55.5934C34.4041 55.5332 34.2935 55.4869 34.1362 55.4547C34.0191 55.4325 33.9043 55.4104 33.7918 55.3882C33.6793 55.366 33.5645 55.342 33.4474 55.3164C33.1884 55.2721 32.991 55.1638 32.8552 54.9919C32.7194 54.8199 32.6517 54.6071 32.6517 54.3523C32.6517 54.1775 32.6811 54.019 32.7395 53.8775C32.7978 53.7359 32.8827 53.616 32.9938 53.5172C33.1049 53.4185 33.2397 53.3429 33.3984 53.2904C33.5571 53.2379 33.7367 53.2114 33.9369 53.2114C34.1899 53.2114 34.4321 53.2528 34.6631 53.3357C34.8941 53.4185 35.0915 53.5331 35.2548 53.6795L34.8619 54.3143C34.5441 54.0407 34.2221 53.9039 33.8954 53.9039C33.7381 53.9039 33.6163 53.9381 33.53 54.0065C33.4437 54.0749 33.4007 54.163 33.4007 54.2714C33.4007 54.3735 33.4292 54.451 33.4861 54.5054C33.5431 54.5594 33.6364 54.6008 33.7661 54.6297C33.8739 54.6519 33.9817 54.6735 34.0895 54.6942C34.1973 54.7149 34.3056 54.7366 34.4134 54.7588C34.7186 54.8127 34.9491 54.9273 35.105 55.1021C35.2604 55.277 35.3383 55.5014 35.3383 55.7749C35.3383 55.9497 35.3061 56.1106 35.2413 56.257C35.1764 56.4034 35.0849 56.5291 34.9659 56.6341C34.8469 56.739 34.7023 56.82 34.531 56.8777C34.3602 56.9351 34.1679 56.9635 33.9551 56.9635L33.9565 56.9625Z"
                  fill="white"
                />
                <path
                  d="M36.2495 56.9053V53.2688H37.8497C38.007 53.2688 38.1516 53.2934 38.2846 53.343C38.4172 53.3921 38.5306 53.46 38.6244 53.5457C38.7182 53.6314 38.7924 53.735 38.8465 53.8559C38.9002 53.9767 38.9272 54.1087 38.9272 54.2522C38.9272 54.4179 38.8843 54.5686 38.798 54.7054C38.7116 54.8421 38.5978 54.9519 38.4559 55.0348V55.068C38.6253 55.1475 38.762 55.2587 38.8652 55.4022C38.9683 55.5457 39.0201 55.7095 39.0201 55.8934C39.0201 56.0398 38.993 56.1752 38.9394 56.2994C38.8852 56.4237 38.8096 56.5311 38.7126 56.6216C38.6155 56.7121 38.4998 56.7825 38.3658 56.8316C38.2319 56.8807 38.0844 56.9057 37.9244 56.9057H36.2504L36.2495 56.9053ZM37.0452 54.7434H37.7573C37.8684 54.7434 37.9575 54.7068 38.0256 54.6336C38.0933 54.5604 38.1274 54.4651 38.1274 54.3471C38.1274 54.2291 38.0942 54.1289 38.028 54.0557C37.9617 53.9825 37.8716 53.9459 37.7573 53.9459H37.0452V54.7429V54.7434ZM37.0452 56.2277H37.8222C37.9426 56.2277 38.0378 56.1887 38.1087 56.1106C38.1796 56.0326 38.2151 55.93 38.2151 55.8029C38.2151 55.6758 38.1796 55.5684 38.1087 55.4904C38.0378 55.4123 37.9421 55.3733 37.8222 55.3733H37.0452V56.2277Z"
                  fill="white"
                />
                <path
                  d="M39.6724 56.9053L40.9212 53.2688H41.8368L43.0669 56.9053H42.2115L41.9478 56.0413V56.0268H40.7639V56.0413L40.4956 56.9053H39.6724ZM40.9814 55.3444V55.3685H41.735V55.3444C41.6118 54.9341 41.4868 54.5281 41.3603 54.1275L40.9809 55.3444H40.9814Z"
                  fill="white"
                />
                <path
                  d="M43.8394 56.9053V53.2688H44.593L46.0266 55.4879V55.4735L46.0126 53.2688H46.7616V56.9053H46.0542L44.5744 54.6144V54.624L44.5884 56.9053H43.8394Z"
                  fill="white"
                />
                <path
                  d="M47.8765 56.9053V53.2688H48.6721V54.858L49.8747 53.2688H50.7997L49.4123 54.9678V54.9871L50.8874 56.9057H49.8654L48.6721 55.2452V56.9057H47.8765V56.9053Z"
                  fill="white"
                />
                <path
                  d="M55.5764 56.963C55.3361 56.963 55.1126 56.9143 54.9058 56.8175C54.6991 56.7203 54.5194 56.5878 54.3668 56.4188C54.2142 56.2502 54.0938 56.0513 54.0061 55.8221C53.9179 55.5929 53.874 55.3482 53.874 55.0872C53.874 54.8262 53.917 54.5772 54.0038 54.3499C54.0901 54.1226 54.2096 53.9242 54.3622 53.7556C54.5148 53.5871 54.6935 53.4541 54.8988 53.3569C55.1037 53.2596 55.3249 53.2114 55.5624 53.2114C55.8368 53.2114 56.0935 53.2711 56.3324 53.3906C56.5714 53.51 56.7646 53.6795 56.913 53.8991L56.3628 54.4433C56.1532 54.1317 55.8863 53.9757 55.5624 53.9757C55.4392 53.9757 55.3244 54.0036 55.218 54.059C55.1116 54.1149 55.0202 54.1919 54.9427 54.2907C54.8657 54.3894 54.8055 54.5069 54.7626 54.6437C54.7192 54.7804 54.6977 54.9283 54.6977 55.0877C54.6977 55.2471 54.7201 55.3949 54.7649 55.5317C54.8097 55.6685 54.8718 55.787 54.952 55.8871C55.0323 55.9873 55.1261 56.0663 55.2344 56.1236C55.3422 56.1809 55.4612 56.2093 55.5904 56.2093C55.772 56.2093 55.9302 56.1515 56.0641 56.035C56.1985 55.9189 56.2867 55.7672 56.3301 55.5794V55.5553H55.5209V54.8349H57.186V55.0737C57.186 55.3444 57.1458 55.5958 57.0656 55.8279C56.9853 56.06 56.8738 56.2599 56.7305 56.427C56.5872 56.5941 56.4174 56.7251 56.2218 56.8204C56.0263 56.9158 55.8112 56.9635 55.5764 56.9635V56.963Z"
                  fill="white"
                />
                <path
                  d="M58.1475 56.9053V53.2688H59.7197C59.8863 53.2688 60.0393 53.2972 60.1798 53.3545C60.3198 53.4118 60.4402 53.4913 60.5405 53.5934C60.6409 53.6955 60.7193 53.8164 60.7762 53.956C60.8331 54.0962 60.8621 54.2503 60.8621 54.4188C60.8621 54.6481 60.8079 54.8518 60.7001 55.0295C60.5923 55.2077 60.4458 55.343 60.261 55.4355V55.4591L60.9269 56.9053H60.0482L59.4658 55.564V55.5544H58.9431V56.9048H58.1475V56.9053ZM58.9427 54.8864H59.6455C59.7687 54.8864 59.8681 54.8441 59.9437 54.7598C60.0193 54.6755 60.0571 54.5647 60.0571 54.428C60.0571 54.2912 60.0193 54.1776 59.9437 54.0962C59.8681 54.0148 59.7687 53.9743 59.6455 53.9743H58.9427V54.886V54.8864Z"
                  fill="white"
                />
                <path
                  d="M63.3131 56.963C63.0788 56.963 62.8599 56.9143 62.6565 56.8175C62.453 56.7203 62.2757 56.5878 62.1245 56.4188C61.9733 56.2502 61.8547 56.0513 61.7684 55.8221C61.6821 55.5929 61.6387 55.3482 61.6387 55.0872C61.6387 54.8262 61.6816 54.5772 61.7684 54.3499C61.8547 54.1226 61.9733 53.9242 62.1245 53.7556C62.2757 53.5871 62.453 53.4541 62.6565 53.3569C62.8599 53.2596 63.0788 53.2114 63.3131 53.2114C63.5473 53.2114 63.7662 53.2601 63.9697 53.3569C64.1731 53.4541 64.3505 53.5871 64.5017 53.7556C64.6529 53.9242 64.7714 54.1226 64.8577 54.3499C64.9441 54.5772 64.987 54.8233 64.987 55.0872C64.987 55.3511 64.9441 55.5929 64.8577 55.8221C64.7714 56.0513 64.6529 56.2502 64.5017 56.4188C64.3505 56.5873 64.1736 56.7203 63.9697 56.8175C63.7662 56.9143 63.5473 56.963 63.3131 56.963ZM63.3131 56.1992C63.4335 56.1992 63.5459 56.1713 63.6505 56.1154C63.7555 56.06 63.846 55.982 63.9235 55.8813C64.0005 55.7812 64.0607 55.6632 64.1036 55.5283C64.1465 55.393 64.1685 55.2461 64.1685 55.0867C64.1685 54.9273 64.147 54.78 64.1036 54.6427C64.0607 54.5059 64.0005 54.3879 63.9235 54.2897C63.8465 54.191 63.7555 54.1139 63.6505 54.0581C63.5455 54.0027 63.433 53.9747 63.3131 53.9747C63.1931 53.9747 63.0802 54.0027 62.9757 54.0581C62.8707 54.1139 62.7806 54.191 62.705 54.2897C62.6294 54.3884 62.5692 54.5059 62.5249 54.6427C62.4801 54.7795 62.4581 54.9273 62.4581 55.0867C62.4581 55.2461 62.4805 55.393 62.5249 55.5283C62.5697 55.6637 62.6299 55.7812 62.705 55.8813C62.7806 55.9815 62.8707 56.0595 62.9757 56.1154C63.0807 56.1713 63.1931 56.1992 63.3131 56.1992Z"
                  fill="white"
                />
                <path
                  d="M67.3179 56.9582C67.099 56.9582 66.9016 56.9202 66.7262 56.8436C66.5507 56.767 66.4004 56.6592 66.2754 56.519C66.1503 56.3789 66.0542 56.2079 65.9865 56.0057C65.9188 55.8039 65.8848 55.5785 65.8848 55.3305V53.2688H66.68V55.3305C66.68 55.5949 66.7378 55.8039 66.8536 55.958C66.9693 56.1126 67.1242 56.1896 67.3184 56.1896C67.5125 56.1896 67.6674 56.1126 67.7832 55.958C67.8989 55.8039 67.9563 55.5944 67.9563 55.3305V53.2688H68.752V55.3305C68.752 55.579 68.7188 55.8039 68.6526 56.0057C68.5863 56.2079 68.4906 56.3789 68.3656 56.519C68.241 56.6592 68.0898 56.767 67.9124 56.8436C67.7351 56.9202 67.5372 56.9582 67.3184 56.9582H67.3179Z"
                  fill="white"
                />
                <path
                  d="M69.8012 56.9053V53.2688H71.3828C71.5554 53.2688 71.7127 53.2991 71.8546 53.3593C71.9964 53.4195 72.1168 53.5024 72.2153 53.6074C72.3138 53.7123 72.3903 53.838 72.4444 53.9844C72.4981 54.1308 72.5252 54.2898 72.5252 54.4617C72.5252 54.624 72.4981 54.7757 72.4444 54.9173C72.3903 55.0589 72.3142 55.1812 72.2153 55.2847C72.1164 55.3883 71.9964 55.4701 71.8546 55.5308C71.7127 55.591 71.5554 55.6213 71.3828 55.6213H70.5964V56.9053H69.8008H69.8012ZM70.5964 54.9438H71.2395C71.4061 54.9438 71.5279 54.8975 71.6049 54.8055C71.6819 54.7131 71.7206 54.597 71.7206 54.4574C71.7206 54.3177 71.6814 54.2021 71.6026 54.1116C71.5237 54.021 71.4047 53.9758 71.2442 53.9758H70.5969V54.9447L70.5964 54.9438Z"
                  fill="white"
                />
              </g>
              <path
                d="M64 6.5H32C17.9167 6.5 6.5 17.9167 6.5 32V64C6.5 78.0833 17.9167 89.5 32 89.5H64C78.0833 89.5 89.5 78.0833 89.5 64V32C89.5 17.9167 78.0833 6.5 64 6.5Z"
                stroke="black"
                stroke-opacity="0.12"
              />
              <path
                d="M67 0.5H29C13.2599 0.5 0.5 13.2599 0.5 29V67C0.5 82.7401 13.2599 95.5 29 95.5H67C82.7401 95.5 95.5 82.7401 95.5 67V29C95.5 13.2599 82.7401 0.5 67 0.5Z"
                stroke="black"
                stroke-opacity="0.05"
              />
            </g>
            <defs>
              <clipPath id="clip0_65681_102253">
                <rect width="96" height="96" fill="white" />
              </clipPath>
              <clipPath id="clip1_65681_102253">
                <rect
                  width="56"
                  height="17"
                  fill="white"
                  transform="translate(20 40)"
                />
              </clipPath>
            </defs>
          </svg>
      </div>
      <p
        style="
          text-align: center;
          color: #7f7f7f;
          line-height: 22px;
          font-size: 14px;
          margin-top: 12px;
          font-family: 'LB-Nu-Medium';
        "
      >
        პროცესის გასაგრძელებლად <br />
        დააჭირეთ ღილაკს
      </p>

      <button
        type="button"
        id="open-in-app"
        style="
          display: flex;
          width: 293px;
          padding: 16px 0;
          justify-content: center;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          background: #191919;
          border-radius: 10px;
          color: white;
          text-align: center;
          text-decoration: none;
          margin-top: 16px;
          font-family: 'LB-Nu-SemiBold';
        "
      >
        აპლიკაციის გახსნა
      </button>

       <a
        id="get-app"
        href="${isIOS() ? appStoreUrl : playStoreUrl}"
        style="
          display: flex;
          width: 293px;
          padding: 16px 0;
          margin-top: 8px;
          justify-content: center;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: 24px;
          background: #EAEAEB;
          border-radius: 10px;
          color: black;
          text-align: center;
          text-decoration: none;
          font-family: 'LB-Nu-SemiBold';
        "
      >
        <span>გადმოწერა</span>
       <div style="width:24px;height:24px">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="width: 24px;height: 24px;">
          <path
            d="M21 13.5V19.5C21 19.6989 20.921 19.8897 20.7803 20.0303C20.6397 20.171 20.4489 20.25 20.25 20.25H3.75C3.55109 20.25 3.36032 20.171 3.21967 20.0303C3.07902 19.8897 3 19.6989 3 19.5V13.5C3 13.3011 3.07902 13.1103 3.21967 12.9697C3.36032 12.829 3.55109 12.75 3.75 12.75C3.94891 12.75 4.13968 12.829 4.28033 12.9697C4.42098 13.1103 4.5 13.3011 4.5 13.5V18.75H19.5V13.5C19.5 13.3011 19.579 13.1103 19.7197 12.9697C19.8603 12.829 20.0511 12.75 20.25 12.75C20.4489 12.75 20.6397 12.829 20.7803 12.9697C20.921 13.1103 21 13.3011 21 13.5ZM11.4694 14.0306C11.539 14.1004 11.6217 14.1557 11.7128 14.1934C11.8038 14.2312 11.9014 14.2506 12 14.2506C12.0986 14.2506 12.1962 14.2312 12.2872 14.1934C12.3783 14.1557 12.461 14.1004 12.5306 14.0306L16.2806 10.2806C16.3503 10.2109 16.4056 10.1282 16.4433 10.0372C16.481 9.94613 16.5004 9.84855 16.5004 9.75C16.5004 9.65145 16.481 9.55387 16.4433 9.46283C16.4056 9.37178 16.3503 9.28906 16.2806 9.21937C16.2109 9.14969 16.1282 9.09442 16.0372 9.0567C15.9461 9.01899 15.8485 8.99958 15.75 8.99958C15.6515 8.99958 15.5539 9.01899 15.4628 9.0567C15.3718 9.09442 15.2891 9.14969 15.2194 9.21937L12.75 11.6897V3C12.75 2.80109 12.671 2.61032 12.5303 2.46967C12.3897 2.32902 12.1989 2.25 12 2.25C11.8011 2.25 11.6103 2.32902 11.4697 2.46967C11.329 2.61032 11.25 2.80109 11.25 3V11.6897L8.78063 9.21937C8.63989 9.07864 8.44902 8.99958 8.25 8.99958C8.05098 8.99958 7.86011 9.07864 7.71937 9.21937C7.57864 9.36011 7.49958 9.55098 7.49958 9.75C7.49958 9.94902 7.57864 10.1399 7.71937 10.2806L11.4694 14.0306Z"
            fill="black"
          />
        </svg></div>
      </a>
    </div>
  `

  // Wait for next tick to ensure DOM is parsed
  setTimeout(() => {
    const btn = document.getElementById('open-in-app')
    if (btn) {
      btn.addEventListener('click', () => {
        attemptDeepLink({
          iosUrl,
          intentUrl,
          appStoreUrl,
          playStoreUrl,
          delay: 2500
        })
      })
    }
  }, 0)
}

// 5) Updated deep‑link entry point for /promotion/*
//    - In normal mobile browsers: auto‑attempt deep link (existing behavior, improved).
//    - In in‑app browsers: show interstitial and deep‑link on user tap.
function tryOpenAppForPromotionPath() {
  try {
    const path = window.location.pathname
    if (!path || !path.startsWith('/promotion')) return false

    const query = window.location.search || ''
    const playStoreUrl =
      'https://play.google.com/store/apps/details?id=ge.lb.mobilebank'
    const appStoreUrl =
      'https://apps.apple.com/us/app/liberty-mobile/id1455225055'

    if (!(isIOS() || isAndroid())) {
      return false
    }

    // In‑app browsers require a user gesture; render interstitial
    if (isInAppBrowser()) {
      renderPromotionOpenInAppPage(path, query)
      return true
    }

    // Normal mobile browsers: auto attempt deep‑link with fallback
    const iosPayload = encodeURI(`${path.replace(/^\//, '')}${query}`)
    const iosUrl = `libertymobilebank://${iosPayload}`

    const host = window.location.host
    const proto = 'https'
    const pathAndQuery = `${path}${query}`.replace(/^\//, '')
    const intentUrl = `intent://${host}/${pathAndQuery}#Intent;scheme=${proto};package=ge.lb.mobilebank;S.browser_fallback_url=${encodeURIComponent(
      playStoreUrl
    )};end`

    attemptDeepLink({
      iosUrl,
      intentUrl,
      appStoreUrl,
      playStoreUrl,
      delay: 2500
    })
    return true
  } catch (_e) {
    return false
  }
}

function processResults(serverConfig, maintenance) {
  setDiffFromServerTime(maintenance && maintenance.startTime)
  setServerConfig(serverConfig)
  setReduxStore()
  setBaseUrls({
    baseUrl: serverConfig.baseUrlForApi,
    staticContentBaseUrl: serverConfig.staticContentBaseUrl,
    authorizationUrl: serverConfig.authorizationUrl,
    libertyCreditUrl: serverConfig.baseUrlForLibertyCreditApi
  })
  setConsole(serverConfig.server)
  activateGoogleAnalitycs(serverConfig.server, serverConfig.gaTrackingCode)
  activateGoogleTagManager(serverConfig.server, serverConfig.gtm_id)
  let um = setUserManagerAuthorizationUrl(
    serverConfig.authorizationUrl,
    serverConfig.clientId
  )
  if (maintenance && maintenance.maintenance) {
    render(
      <MaintenancePage maintenance={maintenance} />,
      document.getElementById('root')
    )
  } else {
    const path = window.location.pathname
    const url = serverConfig.authorizationUrl

    // 1) Handle explicit promotion redirects for DESKTOP ONLY
    if (!isIOS() && !isAndroid()) {
      switch (path) {
        case '/promotion/signup':
          window.location = `${url}/Signup?clientId=accounts${window.location.search}`
          return
        case '/promotion/reset-password':
          window.location = `${url}/ResetPassword?clientId=accounts${window.location.search}`
          return
        case '/promotion/dashboard':
          window.location = `${url}/Authorization/Login${window.location.search}`
          return
      }
    }

    // 2) Mobile deep‑link logic for ALL /promotion/* paths
    if (path && path.startsWith('/promotion') && (isIOS() || isAndroid())) {
      const redirected = tryOpenAppForPromotionPath()
      if (redirected) {
        return // Stop rendering; mobile will open app or go to store
      }
    }

    // 3) Render SPA (desktop + mobile that didn't deep‑link)
    // Note: URL stripping for desktop /promotion/* paths happens inside App component
    // after OIDC auth is loaded to avoid race conditions
    render(
      <Provider store={store}>
        <OidcProvider store={store} userManager={um}>
          <App />
        </OidcProvider>
      </Provider>,
      document.getElementById('root')
    )
  }
}

let xhrMaintenance
let xhrConfig

function determineMaintenance(responseText) {
  if (responseText) {
    let json = JSON.parse(responseText)
    return json
  }
  return { maintenance: false }
}

function requestOnload(e) {
  if (xhrConfig.readyState === 4 && xhrConfig.status === 200) {
    let serverConfig = JSON.parse(xhrConfig.responseText)
    xhrMaintenance = new XMLHttpRequest()
    xhrMaintenance.open(
      'GET',
      `${serverConfig.baseUrlForApi}/infrastructure/v1/maintenance`,
      true
    )
    xhrMaintenance.setRequestHeader('Cache-Control', 'no-cache')
    xhrMaintenance.onload = e => {
      if (
        xhrMaintenance.readyState === 4 &&
        (xhrMaintenance.status === 200 || xhrMaintenance.status === 204)
      )
        processResults(
          serverConfig,
          determineMaintenance(xhrMaintenance.responseText)
        )
      else processResults(serverConfig, false)
    }
    xhrMaintenance.onerror = e => processResults(serverConfig, false)
    xhrMaintenance.send(null)
  }
}

switch (window.location.pathname) {
  case '/silent_renew':
    processSilentRenew()
    break
  case '/signout':
    let authorizeUrl = decodeURIComponent(getQueryVariable('iss'))
    if (authorizeUrl) {
      let um = createUserManager(
        updateObject(userManagerConfig, { authority: authorizeUrl })
      )
      um.removeUser()
    }
    break
  case '/set_language':
    let lang = getQueryVariable('culture')
    setCookie('lang', lang, 300)
    break
  case '/callback':
    render(
      <CallbackComponent
        userManager={createUserManager({
          clockSkew: userManagerConfig.clockSkew
        })} // { clockSkew: userManagerConfig.clockSkew }
        successCallback={() => {
          // detecting trusted devices
          if (window.location.search.includes('trust_enabled=true')) {
            sessionStorage.setItem('TEMP_TRUST_ENABLED', true)
          }

          const url = sessionStorage.getItem('TEMP_REDIRECT_URL')
          if (url) {
            sessionStorage.removeItem('TEMP_REDIRECT_URL')
            window.location = url
          } else window.location = '/'
        }}
        errorCallback={e => {
          if (e.message === 'No matching state found in storage') {
            window.location = '/'
            return
          }
          const location =
            e.message === 'No state in response' ? '/' : '/general-error'
          setBaseUrls({ baseUrl: '/api' })
          logGeneralError(
            JSON.stringify({
              error: e.message.toString(),
              userAgent: window.navigator.userAgent,
              hashLength: window.location.hash ? window.location.hash.length : 0
            }),
            false
          )
            .then(() => (window.location = location))
            .catch(() => (window.location = location))
          return
        }}
      >
        <p />
      </CallbackComponent>,
      document.getElementById('root')
    )
    break
  default:
    fetchConfig()
      .then(requestOnload)
      .catch(e => console.error(xhrConfig.statusText))
    break
}
